// eslint-disable-next-line no-unused-vars
import { useEffect, useState, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { dayjs } from '@mdc/services';
import classNames from 'classnames';
import { debounce } from 'lodash';

const SVGChart = forwardRef((props, ref) => {
    const { totalScans, dailyMax, data } = props;
    const [chart, setChart] = useState(null);
    const canvasWidth = '100%';
    const rectWidth = '1.75%';
    const canvasHeight = 100;

    const dataValues = data && Object.values(data) || [];
    const dataDates = data && Object.keys(data) || [];

    /*
        in order to stay consistent with the design, we should always have a small filled rectangle at the very bottom
        even if the value is 0. so we start from 6 and add this number to all elements
    */
    const startPoint = 6;
    const spaceBetweenRect = 2.75;

    const removeChart = () => {
        if (!ref.current) {
            return;
        }

        const node = ref.current;

        while (node.firstChild) {
            node.removeChild(node.firstChild);
        }
    };
    /*
        Because of the way d3 works we should wait until the container div where the SVG component
        is rendered (documentation says that the function call must be in componentDidMount)
    */

    const onRezize = debounce(() => {
        removeChart();
        setChart(drawChart(dataValues));
    }, 50);

    useEffect(() => {
        window.addEventListener('resize', onRezize);

        return () => {
            window.removeEventListener('resize', onRezize);
        };
    });

    useEffect(() => {
        setChart(drawChart(dataValues));
    }, []);

    /*  we can't modify the svg text using CSS (margins, padding, etc), so we have to use
        SVG's coordinates to move the text to the right. And based on its length, the distance
        from the beginning of the diagram to the line may vary. So we defined this function for this
        purpose.
    */
    const alignSvgText = (value) => {
        if (value >= 0 && value < 10) {
            return 22;
        } else if (value > 9 && value < 100) {
            return 16;
        } else if (value > 99 && value < 1000) {
            return 10;
        } else if (value > 999 && value < 10000) {
            return 4;
        }

        return 0;
    };

    /*
        The chart will always display statistics for the latest 30 days.
        In case the api return data for < 30 days, we still display 30 columns but fill with 0 from data.length to 30
        In case the api return data for > 30 days, we cut the last elements
    */
    const setDataLength = (data) => {
        let noData = false;
        if (data && data.length > 0) {
            let dataLength = data.length;
            data.length = 30;
            data.fill(0, dataLength, data.length);
        } else {
            data = [];
            data.length = 30;
            data.fill(0);
            noData = true;
        }
        return [data, noData];
    };

    const drawChart = (dataParam) => {
        const [data, noData] = setDataLength(dataParam);

        const filledRectClassnames = classNames({
            active: !noData,
            inactive: noData
        }, 'usageChartSvg');
        /*
            Based on the received data array we define a duplicate variable where we calculate
            how much height should a data value take on the specific chart so the element's
            height won't exceed chart's height
        */
        const chartData = data && data.map((el) => {
            let res = Math.floor(el * canvasHeight / totalScans);
            const result = res && res >= 0 ? res : 0;
            if (result < canvasHeight - 10) {
                return result;
            }
            return canvasHeight - 10;
        });

        const dailyMaxChart = data && data.indexOf(dailyMax) >= 0 && chartData[data.indexOf(dailyMax)] ? chartData[data.indexOf(dailyMax)] : 0;

        const svgContainer = d3.select(ref.current)
            .append('svg')
            .attr('viewbox', `0 0 ${canvasWidth} ${canvasHeight}`)
            .attr('width', canvasWidth)
            .attr('height', canvasHeight)
            .attr('x', canvasWidth)
            .attr('y', 0)
            .attr('preserveAspectRatio', 'xMaxYMid')
            .attr('class', filledRectClassnames);

        const toolTip = d3.select(ref.current).append('div')
            .attr('class', 'tooltip')
            .style('opacity', 0);

        const rectGroup = svgContainer.append('g')
            .attr('class', 'rectGroup');

        const markerGroupWrapper = svgContainer.append('g')
            .attr('class', 'markerGroupWrapper');

        const unfilledRectGroup = rectGroup.append('g')
            .attr('class', 'unfilledRect');

        const filledRectGroup = rectGroup.append('g')
            .attr('class', 'filledRect')
            .attr('rx', 5)
            .attr('ry', 5);

        const handleMouseOver = (dataPoint, iteration) => {
            d3.select(this)
                .style('opacity', '.9');

            /*
                    check if we have a date received from the API for all columns
                    as we display 0 to fill 30 array elements, the last X items may be empty
                */
            if (typeof dataDates[iteration] === 'string') {
                const dateFormatted = dayjs(dataDates[iteration]).format('dddd, ll');
                const selectedDataPoint = dataValues[iteration];
                const scansText = `Scans: ${selectedDataPoint}`;

                toolTip.transition()
                    .duration(200)
                    .style('opacity', .9);

                const topAlignment = (canvasHeight - (dataPoint + startPoint) - 36);
                const top = topAlignment > 0 ? topAlignment : null;

                toolTip.text(dateFormatted)
                    .style('left', ((iteration - 3) * spaceBetweenRect) + '%')
                    .style('top', top > 0 && top + 'px' || '0px')
                    .attr('dy', '0em')
                    .append('span')
                    .attr('class', 'hoverScansNo')
                    .text(`\n${scansText}`);
            }
        };

        const handleMouseOut = () => {
            d3.select(this)
                .style('opacity', '1');

            toolTip.transition()
                .duration(500)
                .style('opacity', 0);
        };

        unfilledRectGroup.selectAll('rect')
            .data(chartData).enter()
            .append('rect')
            .attr('width', rectWidth)
            .attr('height', canvasHeight)
            .attr('rx', 5)
            .attr('ry', 5)
            .attr('x', (dataPoint, iteration) => iteration * spaceBetweenRect + '%')
            .on('mouseover', handleMouseOver)
            .on('mouseout', handleMouseOut);

        filledRectGroup.selectAll('rect')
            .data(chartData).enter()
            .append('rect')
            .attr('width', rectWidth)
            .attr('height', (dataPoint) => dataPoint + startPoint)
            .attr('x', (dataPoint, iteration) => iteration * spaceBetweenRect + '%')
            .attr('rx', 5)
            .attr('ry', 5)
            .attr('y', (dataPoint) => canvasHeight - (dataPoint + startPoint))
            .on('mouseover', handleMouseOver)
            .on('mouseout', handleMouseOut);

        const lineWidth = rectGroup.node().getBBox().width;
        const linePrefixLength = 32;
        const leftMargin = 42;
        dailyMax > 0 && markerGroupWrapper.append('line')
            .attr('x1', linePrefixLength)
            .attr('x2', lineWidth  + leftMargin)
            .attr('y1', canvasHeight - (dailyMaxChart + startPoint))
            .attr('y2', canvasHeight - (dailyMaxChart + startPoint))
            .attr('class', 'svgMarker');

        dailyMax > 0 && markerGroupWrapper.append('text')
            .attr('x', alignSvgText(dailyMax))
            .attr('y', canvasHeight - (dailyMaxChart))
            .attr('class', 'svgMarkerText')
            .text(dailyMax.toString());

        markerGroupWrapper.append('line')
            .attr('x1', 32)
            .attr('x2', 40)
            .attr('y1', canvasHeight - startPoint)
            .attr('y2', canvasHeight - startPoint)
            .attr('class', 'svgMarker');

        markerGroupWrapper.append('text')
            .attr('x', 22)
            .attr('y', canvasHeight - startPoint / 2)
            .attr('class', 'svgMarkerText')
            .text('0');
    };

    return (
        <>
            { chart }
        </>
    );
});

SVGChart.displayName = 'SVGChart';
export default SVGChart;

SVGChart.propTypes = {
    totalScans: PropTypes.number.isRequired,
    dailyMax: (props, propName, componentName) => {
        if (!Number.isNaN(props.dailyMax)) {
            if (props.dailyMax.toString().length > 5) {
                return new Error('dailyMax should have a value between 0 and 99999, in ' + propName + ' supplied to ' + componentName);
            }
        }
    },

    dailyAverage: (props, propName, componentName) => {
        if (!Number.isNaN(props.dailyAverage)) {
            if (props.dailyAverage.toString().length > 5) {
                return new Error('dailyAverage should have a value between 0 and 99999, in ' + propName + ' supplied to ' + componentName);
            }
        }
    },
    data: PropTypes.object.isRequired
};
